
import { Module } from '../types';

export const FORMS_VALIDATION_MODULE: Module = {
  id: "advanced-forms",
  title: "Advanced Form Validation",
  description: "Master complex form validation patterns, custom validators, and form libraries",
  duration: "90 minutes",
  difficulty: "advanced",
  content: `
# Advanced Form Validation in React

Learn sophisticated form validation techniques, custom validation logic, and integration with popular form libraries.

## Custom Validation Hooks

\`\`\`jsx
function useValidation(value, validators) {
  const [error, setError] = useState('');
  const [isValid, setIsValid] = useState(true);

  useEffect(() => {
    for (const validator of validators) {
      const result = validator(value);
      if (result !== true) {
        setError(result);
        setIsValid(false);
        return;
      }
    }
    setError('');
    setIsValid(true);
  }, [value, validators]);

  return { error, isValid };
}

// Validator functions
const required = (value) => {
  return value && value.toString().trim() ? true : 'This field is required';
};

const minLength = (min) => (value) => {
  return value && value.length >= min ? true : \`Minimum length is \${min} characters\`;
};

const email = (value) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(value) ? true : 'Please enter a valid email address';
};

const passwordStrength = (value) => {
  const hasUpper = /[A-Z]/.test(value);
  const hasLower = /[a-z]/.test(value);
  const hasNumber = /\d/.test(value);
  const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(value);
  
  if (value.length < 8) return 'Password must be at least 8 characters';
  if (!hasUpper) return 'Password must contain at least one uppercase letter';
  if (!hasLower) return 'Password must contain at least one lowercase letter';
  if (!hasNumber) return 'Password must contain at least one number';
  if (!hasSpecial) return 'Password must contain at least one special character';
  
  return true;
};

// Usage in component
function AdvancedRegistrationForm() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });

  const emailValidation = useValidation(formData.email, [required, email]);
  const passwordValidation = useValidation(formData.password, [required, passwordStrength]);
  const confirmPasswordValidation = useValidation(
    formData.confirmPassword, 
    [
      required, 
      (value) => value === formData.password ? true : 'Passwords do not match'
    ]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const isFormValid = emailValidation.isValid && 
                     passwordValidation.isValid && 
                     confirmPasswordValidation.isValid;

  return (
    <form>
      <div>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="Email"
          className={emailValidation.error ? 'error' : ''}
        />
        {emailValidation.error && <span className="error-text">{emailValidation.error}</span>}
      </div>

      <div>
        <input
          type="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          placeholder="Password"
          className={passwordValidation.error ? 'error' : ''}
        />
        {passwordValidation.error && <span className="error-text">{passwordValidation.error}</span>}
      </div>

      <div>
        <input
          type="password"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange}
          placeholder="Confirm Password"
          className={confirmPasswordValidation.error ? 'error' : ''}
        />
        {confirmPasswordValidation.error && <span className="error-text">{confirmPasswordValidation.error}</span>}
      </div>

      <button type="submit" disabled={!isFormValid}>
        Register
      </button>
    </form>
  );
}
\`\`\`

## Async Validation

\`\`\`jsx
function useAsyncValidation(value, asyncValidator, delay = 500) {
  const [error, setError] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [isValid, setIsValid] = useState(true);

  useEffect(() => {
    if (!value) {
      setError('');
      setIsValid(true);
      setIsValidating(false);
      return;
    }

    setIsValidating(true);
    const timeoutId = setTimeout(async () => {
      try {
        const result = await asyncValidator(value);
        if (result === true) {
          setError('');
          setIsValid(true);
        } else {
          setError(result);
          setIsValid(false);
        }
      } catch (err) {
        setError('Validation error occurred');
        setIsValid(false);
      } finally {
        setIsValidating(false);
      }
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [value, asyncValidator, delay]);

  return { error, isValid, isValidating };
}

// Async validator function
const checkUsernameAvailability = async (username) => {
  const response = await fetch(\`/api/check-username?username=\${username}\`);
  const data = await response.json();
  return data.available ? true : 'Username is already taken';
};

// Usage
function UsernameInput() {
  const [username, setUsername] = useState('');
  const { error, isValid, isValidating } = useAsyncValidation(
    username, 
    checkUsernameAvailability
  );

  return (
    <div>
      <input
        type="text"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        placeholder="Username"
      />
      {isValidating && <span>Checking availability...</span>}
      {error && <span className="error">{error}</span>}
      {isValid && username && !isValidating && <span className="success">✓ Available</span>}
    </div>
  );
}
\`\`\`

## Complex Form State Management

\`\`\`jsx
function useFormValidation(initialState, validationSchema) {
  const [values, setValues] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateField = (name, value) => {
    const fieldValidation = validationSchema[name];
    if (!fieldValidation) return '';

    for (const validator of fieldValidation) {
      const result = validator(value, values);
      if (result !== true) return result;
    }
    return '';
  };

  const validateAllFields = () => {
    const newErrors = {};
    Object.keys(validationSchema).forEach(field => {
      const error = validateField(field, values[field]);
      if (error) newErrors[field] = error;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (name, value) => {
    setValues(prev => ({ ...prev, [name]: value }));
    
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, values[name]);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleSubmit = async (onSubmit) => {
    setIsSubmitting(true);
    const allFieldsTouched = {};
    Object.keys(validationSchema).forEach(field => {
      allFieldsTouched[field] = true;
    });
    setTouched(allFieldsTouched);

    if (validateAllFields()) {
      try {
        await onSubmit(values);
      } catch (error) {
        console.error('Form submission error:', error);
      }
    }
    setIsSubmitting(false);
  };

  return {
    values,
    errors,
    touched,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    isValid: Object.keys(errors).length === 0
  };
}
\`\`\`

## Form Library Integration (React Hook Form)

\`\`\`jsx
import { useForm } from 'react-hook-form';

function ModernForm() {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue
  } = useForm({
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: ''
    }
  });

  const password = watch('password');

  const onSubmit = async (data) => {
    try {
      await submitForm(data);
    } catch (error) {
      console.error('Submission failed:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <input
        {...register('email', {
          required: 'Email is required',
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: 'Invalid email address'
          }
        })}
        type="email"
        placeholder="Email"
      />
      {errors.email && <span>{errors.email.message}</span>}

      <input
        {...register('password', {
          required: 'Password is required',
          minLength: {
            value: 8,
            message: 'Password must be at least 8 characters'
          }
        })}
        type="password"
        placeholder="Password"
      />
      {errors.password && <span>{errors.password.message}</span>}

      <input
        {...register('confirmPassword', {
          required: 'Please confirm your password',
          validate: value => value === password || 'Passwords do not match'
        })}
        type="password"
        placeholder="Confirm Password"
      />
      {errors.confirmPassword && <span>{errors.confirmPassword.message}</span>}

      <button type="submit" disabled={isSubmitting}>
        {isSubmitting ? 'Submitting...' : 'Submit'}
      </button>
    </form>
  );
}
\`\`\`

## Best Practices

1. **Validate on multiple events** - onChange, onBlur, and onSubmit
2. **Provide real-time feedback** - Show validation results as users type
3. **Use debouncing for expensive validations** - Avoid excessive API calls
4. **Consider accessibility** - Use ARIA attributes for screen readers
5. **Handle edge cases** - Empty values, special characters, etc.
6. **Test validation logic thoroughly** - Unit test your validators
  `,
  codeExamples: [
    {
      title: "Custom Validator Function",
      code: `const required = (value) => {
  return value && value.toString().trim() 
    ? true 
    : 'This field is required';
};

const minLength = (min) => (value) => {
  return value && value.length >= min 
    ? true 
    : \`Minimum length is \${min} characters\`;
};`,
      explanation: "Reusable validator functions that return true for valid values or error messages for invalid ones",
      language: "jsx"
    }
  ],
  practice: {
    task: "Create a custom validation hook that supports multiple validators and async validation",
    starter: `function useFieldValidation(value, validators) {
  // TODO: Implement validation logic
}

function ValidatedInput({ validators, ...props }) {
  // TODO: Use the validation hook
}`,
    solution: `function useFieldValidation(value, validators = []) {
  const [error, setError] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [isValidating, setIsValidating] = useState(false);

  useEffect(() => {
    const validate = async () => {
      setIsValidating(true);
      
      for (const validator of validators) {
        try {
          const result = await validator(value);
          if (result !== true) {
            setError(result);
            setIsValid(false);
            setIsValidating(false);
            return;
          }
        } catch (err) {
          setError('Validation error');
          setIsValid(false);
          setIsValidating(false);
          return;
        }
      }
      
      setError('');
      setIsValid(true);
      setIsValidating(false);
    };

    if (value) {
      validate();
    } else {
      setError('');
      setIsValid(true);
      setIsValidating(false);
    }
  }, [value, validators]);

  return { error, isValid, isValidating };
}

function ValidatedInput({ validators, ...props }) {
  const [value, setValue] = useState('');
  const { error, isValid, isValidating } = useFieldValidation(value, validators);

  return (
    <div>
      <input
        {...props}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className={error ? 'error' : isValid ? 'valid' : ''}
      />
      {isValidating && <span>Validating...</span>}
      {error && <span className="error">{error}</span>}
    </div>
  );
}`,
    hints: [
      "Support both sync and async validators",
      "Use useEffect to trigger validation when value changes",
      "Handle loading states for async validation",
      "Return validation status and error messages"
    ],
    estimatedTime: 45
  },
  quickCheck: {
    questions: [
      {
        id: "validation-q1",
        question: "What is the benefit of using custom validation hooks?",
        options: [
          "They make forms faster",
          "They provide reusable validation logic across components",
          "They automatically fix validation errors",
          "They eliminate the need for server-side validation"
        ],
        correctAnswer: 1,
        correctAnswerIndex: 1,
        difficulty: "easy"
      },
      {
        id: "validation-q2",
        question: "When should you perform async validation?",
        options: [
          "On every keystroke",
          "Only on form submission",
          "With debouncing to avoid excessive API calls",
          "Never, it's too slow"
        ],
        correctAnswer: 2,
        correctAnswerIndex: 2,
        difficulty: "medium"
      },
      {
        id: "validation-q3",
        question: "What's a good pattern for handling validation errors?",
        options: [
          "Show all errors at once",
          "Show errors only after field is touched or form is submitted",
          "Never show errors",
          "Show errors before user interaction"
        ],
        correctAnswer: 1,
        correctAnswerIndex: 1,
        difficulty: "medium"
      }
    ]
  }
};
